import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Input } from "#components/atoms/Input/Input";
import { InputSuffix } from "#components/atoms/InputSuffix/InputSuffix";
import { Field } from "#components/molecules/Field/Field";
import { Modal } from "#components/molecules/Modal/Modal";
import { Select } from "#components/molecules/Select/Select";
import { type CreateMaterialsVolumetricPriceDto } from "#modules/materials/infra/createMaterialsVolumetricPrice.api";
import { useCreateMaterialsVolumetricPrice } from "#modules/materials/view/useCreateMaterialsVolumetricPrice";
import { useMaterialGradeDetails } from "#modules/materials/view/useMaterialGradeDetails";
import {
  AddVolumetricMaterialFormSchema,
  type AddVolumetricMaterialFormValues,
} from "#modules/onboardingV2/domain/addVolumetricMaterialFormSchema";
import { useOnboardingAnalytics } from "#modules/onboardingV2/view/useOnboardingAnalytics";
import { AnalyticsEvent } from "#shared/useAnalytics";

const PRICE_UNIT = "€/kg";

type Props = {
  opened: boolean;
  onClose: () => void;
};

const DEFAULT_VALUES: AddVolumetricMaterialFormValues = {
  materialGrade: null,
  name: "",
  price: "",
};

export const AddVolumetricMaterialModal = ({ opened, onClose }: Props) => {
  const { t } = useTranslation();
  const { createMaterialsVolumetricPrice, isPending } = useCreateMaterialsVolumetricPrice();
  const { captureOnboardingEvent } = useOnboardingAnalytics("materialSpecification");
  const { materialGradeDetails } = useMaterialGradeDetails();

  const { control, handleSubmit, reset } = useForm<
    AddVolumetricMaterialFormValues,
    unknown,
    CreateMaterialsVolumetricPriceDto
  >({
    defaultValues: DEFAULT_VALUES,
    resolver: zodResolver(AddVolumetricMaterialFormSchema),
  });

  const materialGradeOptions = materialGradeDetails.map(
    ({ materialGrade: grade, name: label }) => ({
      value: grade,
      label,
    }),
  );

  const handleClose = () => {
    reset(DEFAULT_VALUES);
    onClose();
  };

  const onSubmit = (data: CreateMaterialsVolumetricPriceDto) => {
    createMaterialsVolumetricPrice(data, {
      onSuccess: () => {
        captureOnboardingEvent(AnalyticsEvent.OnboardingMaterialAdded);
        handleClose();
      },
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={t("onboardingV2.materialSpecification.addModal.title")}
      closeText={t("common.cancel")}
      primaryText={t("common.add")}
      onPrimary={handleSubmit(onSubmit)}
      primaryLoading={isPending}
    >
      <Controller
        name="materialGrade"
        control={control}
        render={({ field, fieldState }) => (
          <Field
            label={t("qrl.materialGrade")}
            error={fieldState.error ? t("common.requiredField") : undefined}
          >
            <Select
              value={field.value}
              onChange={field.onChange}
              data={materialGradeOptions}
              placeholder={t(
                "onboardingV2.materialSpecification.addModal.materialGradePlaceholder",
              )}
              error={Boolean(fieldState.error)}
              aria-label={t("qrl.materialGrade")}
            />
          </Field>
        )}
      />
      <Controller
        name="name"
        control={control}
        render={({ field, fieldState }) => (
          <Field
            label={t("qrl.materialSpecification")}
            error={fieldState.error ? t("common.requiredField") : undefined}
          >
            <Input
              value={field.value}
              onChange={field.onChange}
              placeholder={t("onboardingV2.materialSpecification.addModal.namePlaceholder")}
              error={Boolean(fieldState.error)}
              aria-label={t("qrl.materialSpecification")}
            />
          </Field>
        )}
      />
      <Controller
        name="price"
        control={control}
        render={({ field, fieldState }) => (
          <Field
            label={t("onboardingV2.materialSpecification.addModal.priceLabel")}
            error={fieldState.error ? t("common.requiredField") : undefined}
          >
            <InputSuffix
              value={field.value}
              onChange={field.onChange}
              placeholder="0,00"
              suffix={PRICE_UNIT}
              error={Boolean(fieldState.error)}
              aria-label={t("onboardingV2.materialSpecification.addModal.priceLabel")}
            />
          </Field>
        )}
      />
    </Modal>
  );
};
